import {
  ContentMenuActionType,
  IContentMenuActionConfig,
  IContentMenuSubActionConfig,
  IEditabledDataService,
} from '../../../models/types';
import { GridRow } from '../../../services/dto/grid-row';
import { InsertPosition } from '../../../services/dto/insert-position';
import { RowType } from '../../../services/dto/row-type';

export const INSERT_POSITION_SUB_MENU: IContentMenuSubActionConfig[] = [
  { icon: 'arrow-big-top', name: 'grid.editabled.position.above', position: InsertPosition.Above },
  {
    disabled: (data: GridRow) => data?.rowType !== RowType.Chapter,
    icon: 'arrow-go-forward-line',
    name: 'grid.editabled.position.inside',
    position: InsertPosition.Inside,
  },
  { icon: 'arrow-big-bottom', name: 'grid.editabled.position.below', position: InsertPosition.Below },
];

/** Le type de ligne que chaque action d'ajout cree, avec son libelle dans le menu et dans la barre d'ajout. */
export const ADD_ROW_ACTIONS: {
  action: ContentMenuActionType;
  icon: string;
  label: string;
  name: string;
  type: RowType;
}[] = [
  {
    action: 'addChapter',
    icon: 'folders',
    label: 'grid.editabled.add-chapter',
    name: 'grid.editabled.context.add-chapter',
    type: RowType.Chapter,
  },
  {
    action: 'addLine',
    icon: 'add',
    label: 'grid.editabled.add-line',
    name: 'grid.editabled.context.add-line',
    type: RowType.Item,
  },
  {
    action: 'addComment',
    icon: 'chat-1-line',
    label: 'grid.editabled.add-comment',
    name: 'grid.editabled.context.add-comment',
    type: RowType.Comment,
  },
];

export const ALL_ROW_TYPES: RowType[] = ADD_ROW_ACTIONS.map(entry => entry.type);

export interface EditabledMenuContext {
  allowedRowTypes: RowType[];
  rowSelectedForDuplicate: () => string | null;
  rowSelectedForMove: () => string | null;
  // seule la presence des methodes compte : une action non fournie n'apparait pas
  services: Partial<Record<keyof IEditabledDataService<GridRow>, unknown>> | null;
}

// sans chapitre autorise, « inserer dedans » n'a plus de cible
const positionsFor = (allowedRowTypes: RowType[]) =>
  allowedRowTypes.includes(RowType.Chapter)
    ? INSERT_POSITION_SUB_MENU
    : INSERT_POSITION_SUB_MENU.filter(sub => sub.position !== InsertPosition.Inside);

const addItems = ({ allowedRowTypes, services }: EditabledMenuContext): IContentMenuActionConfig[] =>
  services?.addLine$
    ? ADD_ROW_ACTIONS.filter(entry => allowedRowTypes.includes(entry.type)).map(({ action, icon, name }) => ({
        action,
        icon,
        name,
        subMenu: positionsFor(allowedRowTypes),
      }))
    : [];

const convertItems = ({ allowedRowTypes, services }: EditabledMenuContext): IContentMenuActionConfig[] =>
  services?.convertLine$
    ? [
        {
          action: 'convertLine',
          icon: 'arrow-go-forward-line',
          name: 'grid.editabled.context.convert',
          subMenu: ADD_ROW_ACTIONS.filter(entry => allowedRowTypes.includes(entry.type)).map(({ icon, type }) => ({
            disabled: (data: GridRow) => data?.rowType === type,
            icon,
            name: `grid.editabled.row-type.${RowType[type].toLowerCase()}`,
            targetType: type,
          })),
        },
      ]
    : [];

const lineItems = ({ services }: EditabledMenuContext): IContentMenuActionConfig[] => [
  ...(services?.hideLine$
    ? [
        {
          action: 'hideLine' as const,
          icon: (data: GridRow) => (data?.hidden ? 'eye' : 'eye-closed'),
          name: (data: GridRow) => (data?.hidden ? 'grid.editabled.context.show' : 'grid.editabled.context.hide'),
        },
      ]
    : []),
  ...(services?.deleteLine$
    ? [{ action: 'deleteLine' as const, icon: 'delete', name: 'grid.editabled.context.delete' }]
    : []),
];

/** Selection puis placement : une ligne ne peut pas etre deplacee sur elle-meme, mais peut y etre dupliquee. */
export const buildSelectionMenuItems = ({
  allowedRowTypes,
  rowSelectedForDuplicate,
  rowSelectedForMove,
  services,
}: EditabledMenuContext): IContentMenuActionConfig[] => {
  const moveHere = (data: GridRow) => {
    const sourceId = rowSelectedForMove();
    return sourceId === null || data?.id === sourceId;
  };
  return [
    ...(services?.moveLine$
      ? [
          {
            action: 'selectForMove' as const,
            icon: 'clockwise-2-line',
            name: 'grid.editabled.context.select-for-move',
          },
          {
            action: 'moveTo' as const,
            disabled: moveHere,
            icon: 'arrow-right-down-line',
            name: 'grid.editabled.context.move-here',
            subMenu: positionsFor(allowedRowTypes),
          },
        ]
      : []),
    ...(services?.duplicateLineTo$
      ? [
          {
            action: 'selectForDuplicate' as const,
            icon: 'file-copy-2-line',
            name: 'grid.editabled.context.select-for-duplicate',
          },
          {
            action: 'duplicateTo' as const,
            disabled: () => rowSelectedForDuplicate() === null,
            icon: 'arrow-right-down-line',
            name: 'grid.editabled.context.duplicate-here',
            subMenu: positionsFor(allowedRowTypes),
          },
        ]
      : []),
  ];
};

/** Menu d'edition complet, source unique du menu contextuel et des raccourcis clavier. */
export const createEditabledContextMenu = (context: EditabledMenuContext): IContentMenuActionConfig[] => [
  ...addItems(context),
  ...convertItems(context),
  ...lineItems(context),
  ...buildSelectionMenuItems(context),
];
